/**
 * Accepted-quote edit confirmation
 * ================================
 *
 * One gate, shared by every surface that can edit a quote: the inline
 * description / price / notes / line-item editors on the quote detail page and
 * the full edit modal on the quotes and dashboard lists. All of them funnel
 * through TurboEditModal.submit, which is the sole caller of gate() — no edit
 * surface invokes it directly.
 *
 * Why it exists: a tradie who gets a quote accepted on-site and then spots a
 * mistake must be able to fix it. Editing stays allowed; this dialog makes the
 * consequence visible rather than blocking the edit. The checkbox decides
 * whether the correction also lands on the job created at acceptance — ticked
 * by default, because a mistake usually needs fixing in both places.
 *
 * The job the checkbox names, and whether it can still be changed, come from
 * GET /quote/<id>/job-sync-context so the rule lives in the service rather than
 * being restated per template. A completed job is reported, not offered: its
 * invoice has already left draft and no longer re-reads from the job.
 *
 * Markup: templates/components/modals/accepted_quote_edit_confirm.html
 * <!-- Sourced by: static/js/base-core.entry.js -->
 */

(function () {
    'use strict';

    const MODAL_ID = 'acceptedQuoteEditConfirmModal';
    const SYNC_FIELD = 'sync_job';

    const AcceptedQuoteEditConfirm = {
        /**
         * Gate a quote edit behind the accepted-quote confirmation.
         *
         * Resolves true when the caller should go ahead and submit. Non-quote
         * entities and quotes in any other status pass straight through, so
         * call sites need no status branching of their own.
         *
         * On proceed the form carries a `sync_job` field reflecting the
         * checkbox; the field name lives here so the view has one spelling to
         * read. On cancel the field is removed, leaving the form exactly as a
         * non-accepted edit would submit it.
         *
         * `entityId` and `status` arrive undefined from the detail page's
         * inline editors, which render only alongside #quoteData and let it
         * answer both; the list edit modal knows the row it opened and passes
         * them through TurboEditModal.submit.
         *
         * @param {HTMLFormElement} form   the form about to be submitted
         * @param {{entityType: string, entityId: (string|number|undefined), status: (string|undefined)}} ctx
         * @returns {Promise<boolean>}
         */
        async gate(form, ctx) {
            const { entityType } = ctx || {};
            if (entityType !== 'quote') return true;

            const page = this._pageQuote();
            const entityId = (ctx && ctx.entityId) || (page && page.id);
            const status = (ctx && ctx.status !== undefined)
                ? ctx.status
                : (page && String(page.id) === String(entityId) ? page.status : undefined);

            if (!entityId || status !== 'accepted') return true;

            const modalEl = document.getElementById(MODAL_ID);
            // No dialog on this page — fail open rather than trap the edit.
            if (!modalEl || typeof bootstrap === 'undefined') return true;

            const context = await this._fetchJobContext(entityId);
            this._render(modalEl, context);

            const syncJob = await this._awaitChoice(modalEl);
            if (syncJob === null) {
                this._clearSyncField(form);
                return false;
            }
            this._setSyncField(form, syncJob);
            return true;
        },

        /**
         * The quote the current page is about, from its hidden data element.
         *
         * Present on the quote detail page only. Callers that already know
         * which quote they are saving (the list edit modal, opened from a row)
         * pass their own id and status and never consult this.
         */
        _pageQuote() {
            const el = document.getElementById('quoteData');
            if (!el || !el.dataset.id) return null;
            return { id: el.dataset.id, status: el.dataset.status };
        },

        /**
         * Which jobs this edit could reach. A failed lookup returns empty lists
         * so the dialog still appears (the warning is the important half) with
         * the sync option withheld rather than offered on a guess.
         */
        async _fetchJobContext(entityId) {
            try {
                const response = await fetch(`/quote/${entityId}/job-sync-context`, {
                    credentials: 'same-origin',
                    headers: { 'Accept': 'application/json' },
                });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return await response.json();
            } catch (error) {
                console.error('[AcceptedQuoteEditConfirm] job context lookup failed', error);
                return { syncable: [], blocked: [] };
            }
        },

        _render(modalEl, context) {
            const syncable = context.syncable || [];
            const blocked = context.blocked || [];

            const option = modalEl.querySelector('[data-role="sync-option"]');
            const checkbox = modalEl.querySelector('[data-role="sync-checkbox"]');
            const label = modalEl.querySelector('[data-role="sync-label"]');
            const note = modalEl.querySelector('[data-role="blocked-note"]');

            if (syncable.length) {
                option.classList.remove('d-none');
                checkbox.checked = true;
                label.textContent = syncable.length === 1
                    ? `Also update Job #${syncable[0].id} to match`
                    : `Also update the ${syncable.length} linked jobs to match`;
            } else {
                option.classList.add('d-none');
                checkbox.checked = false;
            }

            if (blocked.length) {
                note.classList.remove('d-none');
                note.textContent = blocked.length === 1
                    ? `Job #${blocked[0].id} is ${blocked[0].status.toLowerCase()}, so this edit will not change it or its invoice.`
                    : `${blocked.length} linked jobs are finished, so this edit will not change them or their invoices.`;
            } else {
                note.classList.add('d-none');
            }
        },

        /**
         * Show the dialog and resolve with the checkbox value, or null when the
         * user backs out.
         *
         * The proceed button is replaced rather than re-bound so a listener
         * from a previous open cannot fire twice, and hidden.bs.modal covers
         * every dismissal route (Cancel, the X, Escape, backdrop click).
         */
        _awaitChoice(modalEl) {
            return new Promise((resolve) => {
                const checkbox = modalEl.querySelector('[data-role="sync-checkbox"]');
                const oldBtn = modalEl.querySelector('[data-role="proceed"]');
                const proceedBtn = oldBtn.cloneNode(true);
                oldBtn.parentNode.replaceChild(proceedBtn, oldBtn);

                let proceeded = false;
                const modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);

                proceedBtn.addEventListener('click', () => {
                    proceeded = true;
                    modal.hide();
                });
                modalEl.addEventListener('hidden.bs.modal', () => {
                    resolve(proceeded ? checkbox.checked : null);
                }, { once: true });

                modal.show();
            });
        },

        _setSyncField(form, syncJob) {
            let input = form.querySelector(`[name="${SYNC_FIELD}"]`);
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = SYNC_FIELD;
                form.appendChild(input);
            }
            input.value = syncJob ? 'true' : 'false';
        },

        _clearSyncField(form) {
            const input = form.querySelector(`[name="${SYNC_FIELD}"]`);
            if (input) input.remove();
        },
    };

    window.AcceptedQuoteEditConfirm = AcceptedQuoteEditConfirm;
})();
